import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DateCalendar } from "@/components/dates/DateCalendar";
import { IconCalendar, IconX } from "@/components/icons";
import { useAnchoredOverlay } from "@/hooks/useAnchoredOverlay";
import { useClickOutside } from "@/hooks/useClickOutside";
import {
  displayDateToIso,
  formatIsoDate,
  isoToDisplayDate,
  maskDisplayDate,
} from "@/lib/dates/isoDate";

const inputClassName =
  "w-full border border-slate-200 bg-white font-onest text-slate-800 shadow-sm transition-colors placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 rounded-lg py-1.5 pl-2.5 pr-14 text-sm";

const compactInputClassName =
  "w-full rounded-md border border-slate-200 bg-white py-1.5 pl-2 pr-8 font-onest text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20";

export function AdminDatePicker({
  id,
  value,
  onChange,
  compact = false,
  calendarLabel = "Открыть календарь",
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
  calendarLabel?: string;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => isoToDisplayDate(value));
  const todayIso = formatIsoDate(new Date());

  useEffect(() => {
    setDraft(isoToDisplayDate(value));
  }, [value]);

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close, open, overlayRef);
  useAnchoredOverlay({
    open,
    triggerRef: containerRef,
    overlayRef,
    align: "start",
    matchTriggerWidth: false,
    minHeight: 280,
  });

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const commitDraft = (nextDraft = draft) => {
    if (!nextDraft) {
      onChange("");
      return;
    }
    const iso = displayDateToIso(nextDraft);
    if (iso) onChange(iso);
    else setDraft(isoToDisplayDate(value));
  };

  return (
    <div
      ref={containerRef}
      className={
        compact ? "relative min-w-0 w-full flex-1" : "relative min-w-[11rem]"
      }
    >
      <div className="relative">
        <input
          id={inputId}
          value={draft}
          inputMode="numeric"
          autoComplete="off"
          placeholder="ДД.ММ.ГГГГ"
          onChange={(event) => {
            const next = maskDisplayDate(event.target.value);
            setDraft(next);
            const iso = displayDateToIso(next);
            if (iso) onChange(iso);
            if (!next) onChange("");
          }}
          onFocus={() => setOpen(true)}
          onBlur={(event) => {
            if (containerRef.current?.contains(event.relatedTarget as Node)) {
              return;
            }
            commitDraft();
          }}
          className={compact ? compactInputClassName : inputClassName}
        />
        <div className="absolute inset-y-0 right-0.5 flex items-center">
          {!compact && value ? (
            <button
              type="button"
              title="Очистить"
              aria-label="Очистить дату"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange("");
                setDraft("");
              }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <IconX size={14} />
            </button>
          ) : null}
          <button
            type="button"
            title={calendarLabel}
            aria-label={calendarLabel}
            aria-expanded={open}
            aria-controls={`${inputId}-calendar`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setOpen((current) => !current)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <IconCalendar size={15} />
          </button>
        </div>
      </div>

      {open
        ? createPortal(
            <div
              ref={overlayRef}
              id={`${inputId}-calendar`}
              role="dialog"
              data-admin-overlay=""
              aria-label="Календарь"
              onMouseDown={(event) => event.preventDefault()}
              className="fixed z-[60] w-[17.5rem] rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
              style={{ visibility: "hidden" }}
            >
              <DateCalendar
                value={value}
                onSelect={(iso) => {
                  onChange(iso);
                  setDraft(isoToDisplayDate(iso));
                  setOpen(false);
                }}
                onToday={() => {
                  onChange(todayIso);
                  setDraft(isoToDisplayDate(todayIso));
                  setOpen(false);
                }}
                onClear={() => {
                  onChange("");
                  setDraft("");
                  setOpen(false);
                }}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
